/**
 * Pure view-model for the interactive relevance-graph visualisation — a
 * read-only projection of "everything currently in play" for an incident:
 * which tags/fields are relevant to this incident type, which are actually
 * selected, which `itasc-relevance.ts` edges are currently firing, how
 * responding capability (units/BA operators/water) links to the support
 * tags that request it, and which open insights trace back to which nodes.
 *
 * This is the data half of the graph view; `web/` owns layout/rendering
 * (force simulation, canvas, click handling). Kept here because it's pure
 * and deterministic — the same Incident + Insight[] + DutyConfig always
 * produces the same graph, so it's unit-testable like everything else in
 * `domain/`.
 */

import type { Incident, SupportTag } from './incident.js';
import {
  THREAT_TAGS,
  ACTION_TAGS,
  SUPPORT_TAGS,
  SHOWING,
  SHOWING_LABELS,
  STRATEGIES,
  STRATEGY_LABELS,
  FIRE_STATUS,
  FIRE_STATUS_LABELS,
  labelForTag,
  isConfirmedFire,
  activeIncidentTypes,
} from './incident.js';
import { INCIDENT_TYPE_REGISTRY, INCIDENT_TYPE_LABELS, INCIDENT_TYPES } from './incident-type.js';
import { STAGE_LABELS, INCIDENT_STAGES } from './stages.js';
import { RELEVANCE_EDGES } from './itasc-relevance.js';
import { summariseCapability } from './resources.js';
import { assessWater } from './water.js';
import { baOperatorRequirement } from './doctrine.js';
import type { DutyConfig } from './config.js';
import type { Insight } from './insights/types.js';

export type GraphNodeKind = 'incident' | 'hub' | 'threat' | 'action' | 'support' | 'field-option' | 'capability' | 'insight' | 'stage' | 'type' | 'category';

export interface GraphNode {
  id: string;
  label: string;
  kind: GraphNodeKind;
  /** For field-option nodes: which single-select field cluster this belongs to. */
  group?: string;
  /** Currently selected/realised — drives the "lit up" styling. */
  active: boolean;
  /** A sibling in a single-select cluster that isn't the active one — visually dimmed as incompatible. */
  excluded?: boolean;
  detail?: string;
}

export type GraphEdgeKind = 'hub' | 'member' | 'surface' | 'demote' | 'capability' | 'insight' | 'type-member';

export interface GraphEdgeView {
  id: string;
  source: string;
  target: string;
  kind: GraphEdgeKind;
}

export interface RelevanceGraphView {
  nodes: GraphNode[];
  edges: GraphEdgeView[];
}

/** Support tags whose "how many do we actually have" is worth showing next to the tag itself. */
const CAPABILITY_LINK_SUPPORT_TAGS: SupportTag[] = ['ba-operators', 'additional-tanker', 'bulk-water', 'pumper', 'structural-operators'];

/** This incident type's priority tags (filtered to real, valid tags) union whatever's actually selected — never hides a manually-picked tag just because it wasn't on the priority list. */
function relevantTags<T extends string>(all: readonly T[], priority: readonly string[], selected: readonly T[]): T[] {
  const allSet = new Set<string>(all);
  const seen = new Set<T>();
  const out: T[] = [];
  for (const p of priority) {
    if (allSet.has(p) && !seen.has(p as T)) {
      seen.add(p as T);
      out.push(p as T);
    }
  }
  for (const s of selected) {
    if (!seen.has(s)) {
      seen.add(s);
      out.push(s);
    }
  }
  return out;
}

/**
 * Build the graph view for the current incident. Capped to this incident
 * type's relevant tags (plus anything actually selected) and the top open
 * insights, so the layout stays legible rather than dumping the entire
 * ~50-tag vocabulary on screen regardless of relevance.
 */
export function buildRelevanceGraphView(
  incident: Incident,
  insights: readonly Insight[],
  config: DutyConfig,
): RelevanceGraphView {
  const { itasc } = incident;
  // Union every currently-active type's priority tags (primary + any
  // co-active additional types — e.g. a vehicle fire that's spread into
  // grass) so doctrine/tags for a type added mid-incident actually show up
  // here, not just the type recorded at creation.
  const types = activeIncidentTypes(incident);
  const profiles = types.map((t) => INCIDENT_TYPE_REGISTRY[t]);
  const unionPriority = (pick: (p: (typeof profiles)[number]) => string[]): string[] =>
    Array.from(new Set(profiles.flatMap(pick)));
  const priorityThreatTags = unionPriority((p) => p.priorityThreatTags);
  const priorityActionTags = unionPriority((p) => p.priorityActionTags);
  const prioritySupportTags = unionPriority((p) => p.prioritySupportTags);
  const incidentLabel = types.map((t) => INCIDENT_TYPE_LABELS[t]).join(' + ');

  const nodes: GraphNode[] = [];
  const edges: GraphEdgeView[] = [];
  const nodeIds = new Set<string>();
  const addNode = (n: GraphNode) => {
    if (!nodeIds.has(n.id)) {
      nodeIds.add(n.id);
      nodes.push(n);
    }
  };
  const addEdge = (id: string, source: string, target: string, kind: GraphEdgeKind) => {
    if (nodeIds.has(source) && nodeIds.has(target)) edges.push({ id, source, target, kind });
  };
  const addHub = (id: string, label: string) => addNode({ id, label, kind: 'hub', active: true });

  addNode({ id: 'incident', label: `${incidentLabel} · ${incident.stage}`, kind: 'incident', active: true });

  // Currently-firing relevance edges can surface a tag that isn't on this incident type's priority
  // list and hasn't been selected yet (e.g. a structure fire's "entrapment" surfacing "extrication",
  // an MVA-priority action) — those targets must still get a node, or the surface edge would have
  // nowhere to point.
  const firingEdges = RELEVANCE_EDGES.filter((e) => e.when(itasc));
  const surfacedActions = firingEdges.flatMap((e) => [...(e.surfaceActions ?? []), ...(e.demoteActions ?? [])]);
  const surfacedSupport = firingEdges.flatMap((e) => [...(e.surfaceSupport ?? []), ...(e.demoteSupport ?? [])]);
  const surfacedThreats = firingEdges.flatMap((e) => e.surfaceThreats ?? []);

  const relevantThreats = relevantTags(THREAT_TAGS, [...priorityThreatTags, ...surfacedThreats], itasc.threats.selected);
  const relevantActions = relevantTags(ACTION_TAGS, [...priorityActionTags, ...surfacedActions], itasc.actions.selected);
  const relevantSupport = relevantTags(SUPPORT_TAGS, [...prioritySupportTags, ...surfacedSupport], itasc.support.requested);

  addHub('hub:threats', 'Threats');
  addHub('hub:actions', 'Actions');
  addHub('hub:support', 'Support');
  addEdge('e:hub:threats', 'incident', 'hub:threats', 'hub');
  addEdge('e:hub:actions', 'incident', 'hub:actions', 'hub');
  addEdge('e:hub:support', 'incident', 'hub:support', 'hub');

  for (const t of relevantThreats) {
    const id = `threat:${t}`;
    addNode({ id, label: labelForTag(t), kind: 'threat', active: itasc.threats.selected.includes(t) });
    addEdge(`m:${id}`, 'hub:threats', id, 'member');
  }
  for (const t of relevantActions) {
    const id = `action:${t}`;
    addNode({ id, label: labelForTag(t), kind: 'action', active: itasc.actions.selected.includes(t) });
    addEdge(`m:${id}`, 'hub:actions', id, 'member');
  }
  for (const t of relevantSupport) {
    const id = `support:${t}`;
    addNode({ id, label: labelForTag(t), kind: 'support', active: itasc.support.requested.includes(t) });
    addEdge(`m:${id}`, 'hub:support', id, 'member');
  }

  // Single-select field clusters — mutually exclusive by construction (one Itasc field, one value),
  // which is exactly what "incompatible events disable each other" means for this domain.
  function addFieldCluster<T extends string>(
    hubId: string,
    hubLabel: string,
    values: readonly T[],
    labels: Record<T, string>,
    current: T | undefined,
  ): void {
    addHub(hubId, hubLabel);
    addEdge(`e:${hubId}`, 'incident', hubId, 'hub');
    for (const v of values) {
      const id = `${hubId}:${v}`;
      const active = current === v;
      addNode({ id, label: labels[v], kind: 'field-option', group: hubId, active, excluded: current !== undefined && !active });
      addEdge(`m:${id}`, hubId, id, 'member');
    }
  }
  addFieldCluster('hub:showing', 'Showing', SHOWING, SHOWING_LABELS, itasc.incident.showing);
  addFieldCluster('hub:strategy', 'Strategy', STRATEGIES, STRATEGY_LABELS, itasc.actions.strategy);
  if (isConfirmedFire(incident.status)) {
    addFieldCluster('hub:firestatus', 'Fire status', FIRE_STATUS, FIRE_STATUS_LABELS, itasc.incident.fireStatus);
  }

  // The "developmental relationship" edges themselves — only the tag-triggered ones (an edge with a
  // declared sourceTags), so every drawn link traces to a real, visible cause node.
  for (const edge of firingEdges) {
    if (!edge.sourceTags) continue;
    for (const src of edge.sourceTags) {
      const sourceId = `threat:${src}`;
      if (!nodeIds.has(sourceId)) continue;
      for (const a of edge.surfaceActions ?? []) addEdge(`r:${edge.id}:${sourceId}:${a}`, sourceId, `action:${a}`, 'surface');
      for (const s of edge.surfaceSupport ?? []) addEdge(`r:${edge.id}:${sourceId}:${s}`, sourceId, `support:${s}`, 'surface');
      for (const a of edge.demoteActions ?? []) addEdge(`r:${edge.id}:${sourceId}:${a}:d`, sourceId, `action:${a}`, 'demote');
      for (const s of edge.demoteSupport ?? []) addEdge(`r:${edge.id}:${sourceId}:${s}:d`, sourceId, `support:${s}`, 'demote');
    }
  }

  // Capability — concrete counts (units, BA operators, water) linked to the support tags that
  // request them, so the graph answers "what do we actually have for this?", not just "what's flagged".
  const cap = summariseCapability(incident.resources, config.water.bulkWaterMinL);
  const water = assessWater(incident.waterPlan, incident.resources, config);
  const baReq = baOperatorRequirement(config);
  if (incident.resources.length > 0 || relevantSupport.some((t) => CAPABILITY_LINK_SUPPORT_TAGS.includes(t))) {
    addNode({
      id: 'capability',
      label: 'Capability',
      kind: 'capability',
      active: true,
      detail: `${cap.unitCount} unit(s) · BA ${cap.baOperators}/${baReq.min} · water ${water.limited ? 'limited' : 'ok'}`,
    });
    addEdge('e:capability', 'incident', 'capability', 'hub');
    for (const t of CAPABILITY_LINK_SUPPORT_TAGS) {
      const id = `support:${t}`;
      if (nodeIds.has(id)) addEdge(`c:${id}`, 'capability', id, 'capability');
    }
  }

  // Open insights — "what might need to be thought of" — traced back to whichever tag/field/capability
  // node's label appears in the insight's own `whyShown` strings. Those strings are themselves built
  // from the same labels (see rules.ts / sitrep.ts), so a substring match is an honest, reliable link
  // without requiring every rule to declare its dependencies as separate metadata.
  const openInsights = insights.filter((i) => i.status === 'open' || i.status === 'acknowledged').slice(0, 8);
  const linkableNodes = nodes.filter((n) => n.kind !== 'incident' && n.kind !== 'hub');
  for (const insight of openInsights) {
    const id = `insight:${insight.id}`;
    addNode({ id, label: insight.title, kind: 'insight', active: true, detail: insight.severity });
    addEdge(`e:${id}`, 'incident', id, 'hub');
    const haystack = insight.whyShown.join(' | ').toLowerCase();
    for (const n of linkableNodes) {
      if (haystack.includes(n.label.toLowerCase())) addEdge(`ic:${id}:${n.id}`, id, n.id, 'insight');
    }
  }

  return { nodes, edges };
}

// ── Full taxonomy graph (no incident required) ──────────────────────────────

/**
 * Logistics/support-requirement categories a `SupportTag` belongs to — see
 * `docs/doctrine-research/incident-lifecycle-and-strategy.md` §4. Purely a
 * presentational grouping for the full taxonomy graph; doesn't change any
 * rule/relevance behaviour. A tag not listed here (there shouldn't be one —
 * `buildFullTaxonomyGraphView` asserts every `SupportTag` is covered by
 * construction of this map, but falls back to omitting the category edge
 * rather than throwing if the vocabulary grows without this map keeping up)
 * simply gets no category edge.
 */
const SUPPORT_CATEGORIES: { id: string; label: string; tags: SupportTag[] }[] = [
  { id: 'life-safety', label: 'Life-safety / medical', tags: ['ambulance', 'welfare-rehab'] },
  { id: 'specialist', label: 'Specialist technical capability', tags: ['hazmat-team', 'ses', 'raft', 'aircraft', 'npws-forestry', 'frnsw'] },
  {
    id: 'reinforcement',
    label: 'Firefighting / rescue reinforcement',
    tags: ['additional-tanker', 'bulk-water', 'pumper', 'structural-operators', 'ba-operators', 'ba-cylinders', 'tow-recovery'],
  },
  { id: 'public-order', label: 'Public order / access / investigation', tags: ['police', 'fire-investigator'] },
  { id: 'utilities-env', label: 'Utilities / environment', tags: ['utilities', 'epa'] },
  { id: 'command', label: 'Command / oversight', tags: ['duty-officer'] },
];

/**
 * Build the complete, incident-agnostic taxonomy graph: every `IncidentType`,
 * every lifecycle stage any type uses, the full threat/action/support tag
 * vocabulary, every `RELEVANCE_EDGES` relationship (drawn unconditionally —
 * there's no live ITASC to test `when()` against), and the logistics
 * categories from `docs/doctrine-research/incident-lifecycle-and-strategy.md`
 * §4. Unlike `buildRelevanceGraphView`, this is never filtered to "what's
 * relevant right now" — it's the full logical map of what *could* become
 * relevant, for exploring the taxonomy itself rather than one incident.
 * Pure and deterministic: no incident, no config, same output every call.
 */
export function buildFullTaxonomyGraphView(): RelevanceGraphView {
  const nodes: GraphNode[] = [];
  const edges: GraphEdgeView[] = [];
  const nodeIds = new Set<string>();
  const addNode = (n: GraphNode) => {
    if (!nodeIds.has(n.id)) {
      nodeIds.add(n.id);
      nodes.push(n);
    }
  };
  const addEdge = (id: string, source: string, target: string, kind: GraphEdgeKind) => {
    if (nodeIds.has(source) && nodeIds.has(target) && !edges.some((e) => e.id === id)) {
      edges.push({ id, source, target, kind });
    }
  };

  addNode({ id: 'taxonomy', label: 'All incident types', kind: 'incident', active: true });

  // Canonical lifecycle stages — one node per stage, shared across every type that uses it
  // (see brief §2: every type's stage list is a sub-sequence of the same 8-stage lifecycle).
  addNode({ id: 'hub:stages', label: 'Lifecycle stages', kind: 'hub', active: true });
  addEdge('e:hub:stages', 'taxonomy', 'hub:stages', 'hub');
  for (const stage of INCIDENT_STAGES) {
    const id = `stage:${stage}`;
    addNode({ id, label: STAGE_LABELS[stage], kind: 'stage', active: true });
    addEdge(`m:${id}`, 'hub:stages', id, 'member');
  }

  // Global tag vocabulary hubs — every tag that exists, not just what's relevant to one incident.
  addNode({ id: 'hub:threats', label: 'Threats', kind: 'hub', active: true });
  addNode({ id: 'hub:actions', label: 'Actions', kind: 'hub', active: true });
  addNode({ id: 'hub:support', label: 'Support', kind: 'hub', active: true });
  addEdge('e:hub:threats', 'taxonomy', 'hub:threats', 'hub');
  addEdge('e:hub:actions', 'taxonomy', 'hub:actions', 'hub');
  addEdge('e:hub:support', 'taxonomy', 'hub:support', 'hub');
  for (const t of THREAT_TAGS) {
    const id = `threat:${t}`;
    addNode({ id, label: labelForTag(t), kind: 'threat', active: true });
    addEdge(`m:${id}`, 'hub:threats', id, 'member');
  }
  for (const t of ACTION_TAGS) {
    const id = `action:${t}`;
    addNode({ id, label: labelForTag(t), kind: 'action', active: true });
    addEdge(`m:${id}`, 'hub:actions', id, 'member');
  }
  for (const t of SUPPORT_TAGS) {
    const id = `support:${t}`;
    addNode({ id, label: labelForTag(t), kind: 'support', active: true });
    addEdge(`m:${id}`, 'hub:support', id, 'member');
  }

  // Logistics categories (brief §4) group support tags by what kind of gap they represent.
  addNode({ id: 'hub:categories', label: 'Support categories', kind: 'hub', active: true });
  addEdge('e:hub:categories', 'taxonomy', 'hub:categories', 'hub');
  for (const cat of SUPPORT_CATEGORIES) {
    const id = `category:${cat.id}`;
    addNode({ id, label: cat.label, kind: 'category', active: true });
    addEdge(`m:${id}`, 'hub:categories', id, 'member');
    for (const t of cat.tags) {
      const targetId = `support:${t}`;
      if (nodeIds.has(targetId)) addEdge(`cat:${id}:${targetId}`, id, targetId, 'capability');
    }
  }

  // One node per incident type, linked to the canonical stages it uses and the tags it
  // treats as priority — showing the full type↔stage and type↔tag mapping at once.
  addNode({ id: 'hub:types', label: 'Incident types', kind: 'hub', active: true });
  addEdge('e:hub:types', 'taxonomy', 'hub:types', 'hub');
  for (const type of INCIDENT_TYPES) {
    const profile = INCIDENT_TYPE_REGISTRY[type];
    const id = `type:${type}`;
    addNode({ id, label: INCIDENT_TYPE_LABELS[type], kind: 'type', active: true, detail: profile.defaultLeadAgency });
    addEdge(`m:${id}`, 'hub:types', id, 'member');
    for (const stage of profile.stages) {
      addEdge(`tm:${id}:stage:${stage}`, id, `stage:${stage}`, 'type-member');
    }
    for (const tag of profile.priorityThreatTags) {
      const targetId = `threat:${tag}`;
      if (nodeIds.has(targetId)) addEdge(`tm:${id}:${targetId}`, id, targetId, 'type-member');
    }
    for (const tag of profile.priorityActionTags) {
      const targetId = `action:${tag}`;
      if (nodeIds.has(targetId)) addEdge(`tm:${id}:${targetId}`, id, targetId, 'type-member');
    }
    for (const tag of profile.prioritySupportTags) {
      const targetId = `support:${tag}`;
      if (nodeIds.has(targetId)) addEdge(`tm:${id}:${targetId}`, id, targetId, 'type-member');
    }
  }

  // Every relevance edge, drawn unconditionally (no live ITASC to gate `when()` against) —
  // the complete "what surfaces/demotes what" map, not just what's currently firing.
  for (const edge of RELEVANCE_EDGES) {
    const sources = edge.sourceTags?.map((t) => `threat:${t}`) ?? [];
    if (sources.length === 0) continue; // free-text-keyed edges (e.g. life-reads-clear) have no node to draw from
    for (const sourceId of sources) {
      if (!nodeIds.has(sourceId)) continue;
      for (const t of edge.surfaceThreats ?? []) {
        const targetId = `threat:${t}`;
        if (nodeIds.has(targetId)) addEdge(`r:${edge.id}:${sourceId}:${targetId}`, sourceId, targetId, 'surface');
      }
      for (const a of edge.surfaceActions ?? []) {
        const targetId = `action:${a}`;
        if (nodeIds.has(targetId)) addEdge(`r:${edge.id}:${sourceId}:${targetId}`, sourceId, targetId, 'surface');
      }
      for (const s of edge.surfaceSupport ?? []) {
        const targetId = `support:${s}`;
        if (nodeIds.has(targetId)) addEdge(`r:${edge.id}:${sourceId}:${targetId}`, sourceId, targetId, 'surface');
      }
      for (const a of edge.demoteActions ?? []) {
        const targetId = `action:${a}`;
        if (nodeIds.has(targetId)) addEdge(`r:${edge.id}:${sourceId}:${targetId}:d`, sourceId, targetId, 'demote');
      }
      for (const s of edge.demoteSupport ?? []) {
        const targetId = `support:${s}`;
        if (nodeIds.has(targetId)) addEdge(`r:${edge.id}:${sourceId}:${targetId}:d`, sourceId, targetId, 'demote');
      }
    }
  }

  return { nodes, edges };
}
